import React, { useCallback, useContext, useEffect, useMemo } from "react";
import DropdownInput from "../forms/DropdownInput";
import { RestoreFilterService } from "services/restoreFilters/RestoreFilterService";
import { AppContext } from "services/appContext/AppContext";

export type ColumnFilterChangeEvent = {
    tableIdentifier: string,
    columnId: string,
    filteredValue: string
};

type Props = {
    initialState: {
        identifier: string
    }
    column: {
        filterValue: string;
        preFilteredRows: Array<any>;
        setFilter: (value: string | undefined) => void;
        id: string;
    },
    onChange?: (props: ColumnFilterChangeEvent) => void;
};

export default function TableBooleanColumnFilter(props: Props) {
    const { appContext } = useContext(AppContext);
    const theme = appContext.theme;

    const identifier = props.initialState.identifier;

    useEffect(() => {
        if (RestoreFilterService.hasFilterColumn(identifier, props.column.id)) {
            const storedValue = RestoreFilterService.getFilterValue(identifier, props.column.id);
            if (["Yes", "No"].includes(storedValue)) {
                props.column.setFilter(storedValue);
            }
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [props.column, props.column.filterValue, identifier, RestoreFilterService.getFilterIdentifier(props.initialState.identifier)]);

    const options = useMemo(() => {
        const values = props.column.preFilteredRows.map(row => {
            const cellValue = row.values[props.column.id];
            return cellValue ? "Yes" : "No";
        });

        const uniqueValues = new Set(values);

        const orderedValues = (["Yes", "No"] as const).filter(v => uniqueValues.has(v));

        return [
            { key: "Any", value: "" },
            ...orderedValues.map(v => ({ key: v, value: v }))
        ];
    }, [props.column.id, props.column.preFilteredRows]);

    const handleFilterChange = (filterValue: any, column: any) => {
        props.column.setFilter(filterValue || undefined);
        RestoreFilterService.setFilterValue(props.initialState.identifier, props.column.id, filterValue || "Any");

        if (props.onChange) {
            props.onChange({
                tableIdentifier: identifier,
                columnId: props.column.id,
                filteredValue: filterValue
            });
        }
    };

    const hasValidFilterValue = useCallback(() =>
        props.column.filterValue && props.column.filterValue !== 'Any' && props.column.filterValue !== '',
        [props.column.filterValue]
    );

    return (
        <DropdownInput
            sm
            className={`${hasValidFilterValue() ? "filtered-column_bg" : ""} ${"text-center filter-align"} ${theme === "light" ? "table-text-dark" : ""}`}
            onChange={(e) => handleFilterChange(e.target.value, props.column)}
            value={props.column.filterValue}
            options={options}
        />
    );
}

// Filter function specifically for boolean columns that converts boolean to Yes/No for comparison
export const restoreTableBooleanColumnFilterFunction = (rows: any[], accessor: any, filterValues: any) => {
    return rows.filter((row) => {
        const cellValue = row.values[accessor];
        
        // If filter is "Any" or empty, show all rows
        if (filterValues === '' || filterValues === 'Any') {
            return true;
        }
        
        // Convert boolean value to Yes/No string for comparison
        const displayValue = cellValue ? 'Yes' : 'No';
        
        return displayValue === filterValues;
    });
}